import React from "react";
import { Palette } from "../holidayConfig";
import { REHN_SYMBOL_PATH, REHN_SYMBOL_VIEWBOX } from "../../RehnLogo";
import { BreakdanceSanta, DancingSanta, SleighSanta } from "./Santa";
import { mix } from "./kit";
import { range } from "./sceneUtils";
import { Blush, Contact, Eye, Shaded, ShadeColors, Sparkle, Sphere, useToonId } from "./toon";

/*
 * Julens figurer: ren, snögubbe och paket. Tecknade med mjuk
 * cel-skuggning, glansiga ögon och kontaktskuggor (se toon.tsx).
 */

const FUR: ShadeColors = { base: "#a0603f", shade: "#6e3a26", light: "#e0a67c", rim: "#c47e5a" };
const CREAM: ShadeColors = { base: "#f8e7d8", shade: "#dbbca4", light: "#ffffff" };
const MUZZLE: ShadeColors = { base: "#ecc6a6", shade: "#c99a7a", light: "#fff1e4" };
const HOOF = "#2b150d";
const ANTLER = "#f3dcc4";
const ANTLER_SHADE = "#c9a286";

const knit = (p: Palette): ShadeColors => ({ base: p[600], shade: p[800], light: p[400], rim: p[500] });

/** Ren i halvprofil (tittar åt höger) med röd mule och stickad halsduk. (0, 0) = hovarna. */
export const Reindeer: React.FC<{ x: number; y: number; s?: number; flip?: boolean; p: Palette }> = ({ x, y, s = 1, flip, p }) => {
  const leg = (lx: number, top: number, w: number, c: ShadeColors) => (
    <g>
      <Shaded d={`M${lx - w / 2} ${top} C ${lx - w / 2} ${top + 20}, ${lx - w * 0.4} -14, ${lx - w * 0.42} -6 L ${lx + w * 0.42} -6 C ${lx + w * 0.4} -14, ${lx + w / 2} ${top + 20}, ${lx + w / 2} ${top} Z`} c={c} depth={4} />
      <path d={`M${lx - w * 0.5} -7 L ${lx + w * 0.5} -7 L ${lx + w * 0.56} 0 L ${lx - w * 0.56} 0 Z`} fill={HOOF} />
    </g>
  );
  const farFur: ShadeColors = { base: mix(FUR.base, FUR.shade, 0.5), shade: mix(FUR.shade, "#000000", 0.2), light: FUR.base };
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      <Contact cx={-4} cy={1} rx={66} opacity={0.4} color={p[950]} />
      {/* Ben bakom kroppen */}
      {leg(-30, -50, 11, farFur)}
      {leg(26, -50, 11, farFur)}
      {/* Svans */}
      <path d="M-58 -74 C -70 -84, -76 -74, -70 -68 C -76 -64, -68 -56, -60 -62 Z" fill="#fff8f2" />
      {/* Kropp */}
      <Shaded
        d="M-62 -66 C -64 -90, -34 -100, -4 -96 C 26 -92, 48 -84, 48 -62 C 48 -42, 26 -36, -4 -36 C -36 -36, -60 -44, -62 -66 Z"
        c={FUR}
        depth={12}
        highlight={{ cx: -22, cy: -86, rx: 26, ry: 7, rot: -6, opacity: 0.45 }}
      />
      <path d="M-44 -42 C -26 -33, 14 -33, 34 -42 C 26 -35, 6 -32, -8 -32 C -26 -32, -38 -36, -44 -42 Z" fill={CREAM.base} opacity="0.9" />
      {/* Ben framför */}
      {leg(-44, -52, 12, FUR)}
      {leg(34, -52, 12, FUR)}
      {/* Hals */}
      <Shaded d="M10 -86 C 14 -104, 24 -120, 36 -128 L 58 -116 C 50 -102, 44 -84, 40 -62 Z" c={FUR} depth={8} />
      {/* Bröstpäls */}
      <Shaded
        d="M22 -104 q7 -5 12 0 q7 -3 10 3 q6 3 3 10 q5 6 -1 11 q2 7 -6 8 q-4 7 -11 2 q-7 2 -9 -5 q-7 -3 -3 -10 q-4 -6 1 -11 q-1 -7 4 -8 Z"
        c={CREAM}
        depth={5}
      />
      {/* Öron */}
      <g>
        <Shaded d="M30 -142 C 12 -154, 2 -150, 4 -144 C 10 -136, 22 -134, 32 -136 Z" c={FUR} depth={4} />
        <path d="M27 -141 C 16 -148, 9 -146, 10 -143 C 14 -139, 20 -138, 27 -139 Z" fill="#f1b9a4" />
        <Shaded d="M66 -144 C 82 -158, 94 -156, 92 -150 C 88 -142, 76 -138, 66 -138 Z" c={FUR} depth={4} />
        <path d="M69 -143 C 80 -152, 88 -151, 87 -148 C 84 -144, 76 -141, 69 -141 Z" fill="#f1b9a4" />
      </g>
      {/* Horn */}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <g stroke={ANTLER_SHADE} strokeWidth="7" transform="translate(1.5 1.5)">
          <path d="M40 -146 C 36 -164, 28 -176, 16 -184 M 32 -168 C 22 -168, 16 -172, 12 -178 M 35 -160 C 28 -158, 22 -156, 18 -152" />
          <path d="M60 -148 C 66 -166, 74 -178, 88 -184 M 70 -170 C 78 -172, 82 -178, 82 -186 M 66 -160 C 74 -160, 80 -158, 84 -154" />
        </g>
        <g stroke={ANTLER} strokeWidth="7">
          <path d="M40 -146 C 36 -164, 28 -176, 16 -184 M 32 -168 C 22 -168, 16 -172, 12 -178 M 35 -160 C 28 -158, 22 -156, 18 -152" />
          <path d="M60 -148 C 66 -166, 74 -178, 88 -184 M 70 -170 C 78 -172, 82 -178, 82 -186 M 66 -160 C 74 -160, 80 -158, 84 -154" />
        </g>
        <g stroke="#ffffff" strokeWidth="2" opacity="0.7">
          <path d="M37 -150 C 34 -164, 27 -174, 18 -181" />
          <path d="M58 -152 C 63 -166, 70 -176, 84 -182" />
        </g>
      </g>
      {/* Huvud */}
      <Shaded
        d="M24 -128 C 22 -148, 36 -156, 50 -155 C 66 -154, 76 -144, 74 -128 C 72 -112, 62 -104, 48 -104 C 34 -104, 25 -112, 24 -128 Z"
        c={FUR}
        depth={9}
        highlight={{ cx: 40, cy: -146, rx: 12, ry: 5, rot: -15, opacity: 0.5 }}
      />
      {/* Mule och nos */}
      <Shaded d="M50 -116 C 50 -128, 64 -130, 76 -126 C 88 -122, 90 -110, 82 -104 C 74 -98, 56 -100, 52 -106 C 50 -109, 50 -112, 50 -116 Z" c={MUZZLE} depth={5} />
      <circle cx={86} cy={-120} r={18} fill={p[400]} opacity={0.22} filter="url(#toon-softer)" className="hol-breathe" />
      <Sphere cx={86} cy={-120} r={9} c={{ base: p[500], shade: p[800], light: p[200], rim: p[600] }} />
      <path d="M66 -106 C 70 -102, 76 -102, 79 -106" stroke="#5a2a1a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      {/* Ögon */}
      <Eye x={39} y={-133} rx={5} ry={7.2} look={[0.3, 0.05]} />
      <Eye x={57} y={-134} rx={6.6} ry={8.4} look={[0.3, 0.05]} />
      <path d="M33 -145 C 36 -147, 40 -147, 43 -145" stroke="#4a2616" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M51 -147 C 55 -149, 60 -149, 64 -146" stroke="#4a2616" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <Blush x={46} y={-116} r={6} color={p[400]} opacity={0.45} />
      {/* Stickad halsduk */}
      <Shaded d="M12 -94 C 24 -84, 44 -84, 58 -100 L 62 -90 C 48 -74, 22 -74, 10 -84 Z" c={knit(p)} depth={4} />
      <Shaded d="M44 -84 C 46 -70, 44 -58, 48 -48 L 38 -46 C 34 -58, 34 -70, 36 -82 Z" c={knit(p)} depth={4} />
      <path d="M16 -88 C 28 -80, 44 -80, 58 -94 M 37 -66 L 46 -67 M 36 -58 L 46 -59" stroke={p[100]} strokeWidth="2.4" fill="none" opacity="0.85" />
      <path d="M39 -47 L 38 -41 M 42 -47 L 42 -40 M 45 -48 L 46 -41" stroke={p[700]} strokeWidth="2" strokeLinecap="round" />
      {/* Bjällra */}
      <Sphere cx={30} cy={-80} r={5.5} c={{ base: "#f0dede", shade: "#a88686", light: "#ffffff" }} />
    </g>
  );
};

/** Snögubbe med stickad mössa, halsduk och vantar. (0, 0) = marken. */
export const Snowman: React.FC<{ x: number; y: number; s?: number; p: Palette }> = ({ x, y, s = 1, p }) => {
  const snow: ShadeColors = { base: "#fffafa", shade: mix("#e9cfd2", p[300], 0.25), light: "#ffffff", rim: "#f7e1e1" };
  const coal = { base: "#2a1c1c", shade: "#0d0707", light: "#6b5a5a" };
  const twig = "#4b2c1d";
  const mitten = knit(p);
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <Contact cx={4} cy={2} rx={62} opacity={0.4} color={p[950]} />
      {/* Armar */}
      <path d="M-28 -122 C -44 -132, -56 -142, -66 -154 M -52 -140 L -60 -128 M -58 -146 C -62 -154, -62 -160, -60 -166" stroke={twig} strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M28 -124 C 44 -134, 58 -146, 64 -160 M 52 -142 L 64 -138" stroke={twig} strokeWidth="4" strokeLinecap="round" fill="none" />
      <Shaded d="M58 -172 C 56 -182, 70 -184, 72 -174 C 74 -166, 70 -158, 64 -158 C 58 -158, 56 -164, 58 -172 Z" c={mitten} depth={4} />
      <Shaded d="M54 -170 C 50 -172, 50 -166, 56 -164 Z" c={mitten} depth={2} />
      {/* Kroppen */}
      <Sphere cx={0} cy={-48} r={48} c={snow} />
      <ellipse cx={0} cy={-90} rx={28} ry={6} fill={p[900]} opacity={0.16} filter="url(#toon-soft)" />
      <Sphere cx={0} cy={-116} r={35} c={snow} />
      <ellipse cx={0} cy={-148} rx={20} ry={5} fill={p[900]} opacity={0.18} filter="url(#toon-soft)" />
      <Sphere cx={0} cy={-172} r={27} c={snow} />
      {/* Knappar */}
      {[-126, -110, -94].map(cy => (
        <Sphere key={cy} cx={2} cy={cy} r={4.2} c={coal} />
      ))}
      {/* Halsduk */}
      <Shaded d="M-30 -150 C -12 -140, 14 -140, 31 -150 L 32 -138 C 14 -128, -14 -128, -31 -138 Z" c={knit(p)} depth={5} />
      <Shaded d="M12 -138 C 16 -124, 14 -110, 22 -98 L 10 -94 C 5 -108, 3 -122, 4 -136 Z" c={knit(p)} depth={4} />
      <path d="M-27 -145 C -10 -136, 12 -136, 28 -145 M 8 -118 L 18 -120 M 9 -108 L 20 -110" stroke={p[100]} strokeWidth="2.6" fill="none" opacity="0.85" />
      <path d="M11 -95 L 9 -88 M 15 -96 L 14 -88 M 19 -97 L 20 -89" stroke={p[700]} strokeWidth="2.2" strokeLinecap="round" />
      {/* Ansikte */}
      <Sphere cx={-9} cy={-177} r={3.6} c={coal} />
      <Sphere cx={10} cy={-177} r={3.6} c={coal} />
      <circle cx={-10} cy={-178.5} r={1.2} fill="#ffffff" />
      <circle cx={9} cy={-178.5} r={1.2} fill="#ffffff" />
      <path d="M-14 -185 C -11 -187, -7 -187, -4 -185 M 5 -185 C 8 -187, 12 -187, 15 -185" stroke={coal.base} strokeWidth="1.8" strokeLinecap="round" fill="none" />
      <Blush x={-15} y={-166} r={5} color={p[400]} opacity={0.5} />
      <Blush x={16} y={-166} r={5} color={p[400]} opacity={0.5} />
      <path d="M1 -172 C 10 -171, 22 -168, 30 -164 C 22 -163, 10 -163, 1 -166 Z" fill="#f2853a" />
      <path d="M1 -169 C 10 -168, 22 -166, 30 -164 C 22 -163, 10 -163, 1 -166 Z" fill="#c9591d" opacity="0.6" />
      <path d="M10 -171 L 11 -165 M 18 -169 L 19 -164" stroke="#b44e18" strokeWidth="1" />
      {[-9, -4.5, 0, 4.5, 9].map((dx, k) => (
        <circle key={k} cx={dx + 1} cy={-158 + (dx * dx) * -0.04} r={1.8} fill={coal.base} />
      ))}
      {/* Stickad mössa med tofs */}
      <Shaded d="M-25 -186 C -26 -210, -12 -222, 2 -222 C 18 -222, 28 -208, 27 -186 Z" c={knit(p)} depth={6} />
      <path d="M-18 -204 C -6 -210, 10 -210, 22 -204 M -12 -214 C -2 -218, 8 -218, 16 -214" stroke={p[100]} strokeWidth="2.4" fill="none" opacity="0.85" />
      <Shaded d="M-29 -190 C -29 -196, 30 -196, 31 -190 L 31 -182 C 31 -178, -29 -178, -29 -182 Z" c={{ base: "#fff4f4", shade: p[200], light: "#ffffff" }} depth={3} />
      {range(9).map(k => (
        <line key={k} x1={-24 + k * 6.5} y1={-192} x2={-24 + k * 6.5} y2={-181} stroke={p[200]} strokeWidth="1" />
      ))}
      <Sphere cx={4} cy={-226} r={10} c={{ base: "#fff8f8", shade: p[200], light: "#ffffff" }} />
      {/* Glitter i snön */}
      <Sparkle x={-26} y={-60} r={3} className="hol-star-group" />
      <Sparkle x={-14} y={-128} r={2.4} className="hol-star-group hol-star-group-1" />
      <Sparkle x={22} y={-36} r={2.2} className="hol-star-group hol-star-group-2" />
    </g>
  );
};

export interface GiftSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  d: number;
  box: [string, string, string];
  ribbon: [string, string];
}

/** Paket i 3D med rosett. (x, y) = främre nedre hörnet. */
export const Gift: React.FC<{ g: GiftSpec; shadow: string }> = ({ g, shadow }) => {
  const { x, y, w, h, d, box, ribbon } = g;
  const id = useToonId("gift");
  const dx = d * 0.62;
  const dy = d * 0.42;
  const top = y - h;
  const rb = Math.max(4, w * 0.15);
  const cx = x + w / 2;
  const k = w / 60;
  return (
    <g>
      <defs>
        <linearGradient id={`${id}-f`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={mix(box[1], "#ffffff", 0.18)} />
          <stop offset="100%" stopColor={mix(box[1], box[2], 0.35)} />
        </linearGradient>
        <linearGradient id={`${id}-s`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={box[2]} />
          <stop offset="100%" stopColor={mix(box[2], "#000000", 0.25)} />
        </linearGradient>
      </defs>
      <Contact cx={x + w * 0.62} cy={y + 1} rx={w * 0.8} ry={h * 0.13} color={shadow} opacity={0.45} />
      <path d={`M${x + w} ${y} L${x + w + dx} ${y - dy} L${x + w + dx} ${top - dy} L${x + w} ${top} Z`} fill={`url(#${id}-s)`} />
      <rect x={x} y={top} width={w} height={h} fill={`url(#${id}-f)`} />
      <path d={`M${x} ${top} L${x + dx} ${top - dy} L${x + w + dx} ${top - dy} L${x + w} ${top} Z`} fill={mix(box[0], "#ffffff", 0.1)} />
      {/* Band med glansrand */}
      <rect x={cx - rb / 2} y={top} width={rb} height={h} fill={ribbon[0]} />
      <rect x={cx - rb / 2 + rb * 0.18} y={top} width={rb * 0.18} height={h} fill="#ffffff" opacity="0.45" />
      <path d={`M${cx - rb / 2} ${top} L${cx - rb / 2 + dx} ${top - dy} L${cx + rb / 2 + dx} ${top - dy} L${cx + rb / 2} ${top} Z`} fill={ribbon[0]} />
      <path d={`M${x + w} ${top + h * 0.42} L${x + w + dx} ${top + h * 0.42 - dy} L${x + w + dx} ${top + h * 0.42 - dy + rb} L${x + w} ${top + h * 0.42 + rb} Z`} fill={ribbon[1]} />
      <path d={`M${x + dx * 0.5} ${top - dy * 0.5} L${x + w + dx * 0.5} ${top - dy * 0.5}`} stroke={ribbon[1]} strokeWidth={rb * 0.75} />
      {/* Rosett */}
      <g transform={`translate(${cx + dx / 2} ${top - dy / 2}) scale(${k})`}>
        <Shaded d="M0 0 C -6 12, -14 20, -20 26 L -12 28 C -6 20, -2 10, 0 0 Z" c={{ base: ribbon[1], shade: mix(ribbon[1], "#000000", 0.25), light: "#ffffff" }} depth={2} />
        <Shaded d="M0 0 C 5 12, 12 20, 18 27 L 26 24 C 18 18, 6 10, 0 0 Z" c={{ base: ribbon[1], shade: mix(ribbon[1], "#000000", 0.25), light: "#ffffff" }} depth={2} />
        <Shaded d="M0 0 C -8 -20, -34 -24, -32 -8 C -30 4, -12 4, 0 0 Z" c={{ base: ribbon[0], shade: ribbon[1], light: "#ffffff" }} depth={5} highlight={{ cx: -20, cy: -14, rx: 6, ry: 3, rot: -20, opacity: 0.6 }} />
        <Shaded d="M0 0 C 8 -20, 34 -24, 32 -8 C 30 4, 12 4, 0 0 Z" c={{ base: ribbon[0], shade: ribbon[1], light: "#ffffff" }} depth={5} highlight={{ cx: 16, cy: -15, rx: 6, ry: 3, rot: 20, opacity: 0.6 }} />
        <path d="M-6 -4 C -12 -12, -22 -14, -24 -8" stroke={ribbon[1]} strokeWidth="2" fill="none" opacity="0.6" />
        <path d="M6 -4 C 12 -12, 22 -14, 24 -8" stroke={ribbon[1]} strokeWidth="2" fill="none" opacity="0.6" />
        <Sphere cx={0} cy={-1} r={6} c={{ base: ribbon[0], shade: ribbon[1], light: "#ffffff" }} />
      </g>
      <path d={`M${x} ${top} L${x + w} ${top} L${x + w + dx} ${top - dy}`} stroke="#ffffff" strokeOpacity="0.35" strokeWidth="1" fill="none" />
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Tomten i släden med renspann                                         */
/* ------------------------------------------------------------------ */

interface LegProps {
  x: number;
  y: number;
  /** Framben böjer knät bakåt, bakben böjer hasen framåt. */
  kind: "front" | "back";
  dur: number;
  begin: number;
  far?: boolean;
}

/**
 * Ett ben i två delar som galopperar: låret svänger kring höften/bogen och
 * underbenet viker sig i knät när benet förs framåt, precis som en ren i
 * full fart. Benen hänger neråt – renen springer genom luften.
 */
const GallopLeg: React.FC<LegProps> = ({ x, y, kind, dur, begin, far }) => {
  const upper = 17;
  const lower = 19;
  const w = kind === "back" ? 10 : 8;
  const thigh: ShadeColors = far
    ? { base: mix(FUR.shade, "#000000", 0.1), shade: mix(FUR.shade, "#000000", 0.35), light: FUR.shade }
    : FUR;
  const shin = far ? mix("#8a5a44", "#000000", 0.3) : "#b98363";
  const swing = kind === "front" ? "-38; 26; -38" : "-24; 34; -24";
  const bend = kind === "front" ? "12; 0; 78; 12" : "-8; 0; -62; -8";
  const common = { dur: `${dur}s`, begin: `${begin}s`, repeatCount: "indefinite" };
  return (
    <g transform={`translate(${x} ${y})`}>
      <g>
        <animateTransform attributeName="transform" type="rotate" values={swing} keyTimes="0; 0.5; 1" calcMode="spline" keySplines="0.4 0 0.6 1; 0.4 0 0.6 1" {...common} />
        <Shaded d={`M${-w / 2} -4 C ${-w / 2} ${upper * 0.5}, ${-w * 0.34} ${upper}, ${-w * 0.28} ${upper + 1} L ${w * 0.28} ${upper + 1} C ${w * 0.34} ${upper}, ${w / 2} ${upper * 0.5}, ${w / 2} -4 Z`} c={thigh} depth={3} />
        <g transform={`translate(0 ${upper})`}>
          <g>
            <animateTransform attributeName="transform" type="rotate" values={bend} keyTimes="0; 0.4; 0.75; 1" {...common} />
            <path d={`M${-w * 0.28} 0 L ${-w * 0.22} ${lower - 4} L ${w * 0.22} ${lower - 4} L ${w * 0.28} 0 Z`} fill={shin} />
            <circle cx="0" cy="0" r={w * 0.3} fill={shin} />
            {/* Pälstofs och klöv */}
            <path d={`M${-w * 0.36} ${lower - 6} q ${w * 0.36} 3 ${w * 0.72} 0 l -1 3 q ${-w * 0.3} 2 ${-w * 0.6} 0 Z`} fill={far ? "#cdb8a8" : "#f6e6d8"} />
            <path d={`M${-w * 0.3} ${lower - 3} L ${w * 0.32} ${lower - 3} L ${w * 0.38} ${lower + 1} C ${w * 0.1} ${lower + 2.4}, ${-w * 0.1} ${lower + 2.4}, ${-w * 0.36} ${lower + 1} Z`} fill={HOOF} />
          </g>
        </g>
      </g>
    </g>
  );
};

/**
 * Namnband över renens bål: ett sidenband i temats röda med guldkant och
 * namnet i guld. När släden flygs spegelvänd vänds texten tillbaka.
 */
const NameBand: React.FC<{ name: string; p: Palette; mirrored?: boolean }> = ({ name, p, mirrored }) => (
  <g>
    <path d="M-30 -9 C -12 -12, 12 -12, 32 -8 L 30 5 C 10 1, -12 1, -30 4 Z" fill={p[900]} opacity="0.45" transform="translate(1 1.6)" />
    <path d="M-30 -9 C -12 -12, 12 -12, 32 -8 L 30 5 C 10 1, -12 1, -30 4 Z" fill={p[700]} />
    <path d="M-30 -6.6 C -12 -9.6, 12 -9.6, 31.6 -5.6 M -30 1.6 C -12 -1.4, 12 -1.4, 30.4 2.6" stroke="#f6dfa6" strokeWidth="0.9" fill="none" />
    <path d="M-28 -8 C -12 -11, 12 -11, 30 -7.2" stroke="#ffffff" strokeWidth="0.8" fill="none" opacity="0.35" />
    <path d="M-30 -9 l -6 2 l 3 5 l -3 6 l 6 0 Z" fill={p[800]} />
    <text
      x="1"
      y="-0.6"
      textAnchor="middle"
      fontFamily="Georgia, 'Times New Roman', serif"
      fontStyle="italic"
      fontWeight="700"
      fontSize="9.5"
      letterSpacing="0.4"
      fill="#fbe7b0"
      stroke={p[900]}
      strokeWidth="0.35"
      paintOrder="stroke"
      transform={mirrored ? "translate(2 0) scale(-1 1)" : undefined}
    >
      {name}
    </text>
  </g>
);

/** Flygande ren i full galopp (tittar åt höger). */
const FlyingDeer: React.FC<{ x: number; y: number; p: Palette; lead?: boolean; phase: number; s?: number; name?: string; mirrored?: boolean }> = ({
  x,
  y,
  p,
  lead,
  phase,
  s = 1,
  name,
  mirrored,
}) => {
  const dur = 0.62;
  const b = -phase * dur;
  const harness: ShadeColors = { base: p[600], shade: p[800], light: p[300] };
  const bell: ShadeColors = { base: "#f7e2a6", shade: "#b8892e", light: "#fff8dc" };
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <g className="hol-gallop" style={{ animationDelay: `${b}s`, animationDuration: `${dur}s` }}>
        {/* Bortre benen */}
        <GallopLeg x={20} y={4} kind="front" dur={dur} begin={b - dur * 0.12} far />
        <GallopLeg x={-24} y={4} kind="back" dur={dur} begin={b - dur * 0.62} far />
        {/* Svans */}
        <Shaded d="M-35 -10 C -46 -22, -52 -10, -44 -4 C -42 -2, -38 -3, -35 -6 Z" c={CREAM} depth={2} />
        {/* Kropp */}
        <Shaded
          d="M-38 -2 C -40 -16, -22 -24, 0 -23 C 20 -22, 34 -18, 40 -6 C 44 4, 36 14, 18 15 C 0 16, -22 15, -32 10 C -37 7, -38 3, -38 -2 Z"
          c={FUR}
          depth={8}
          highlight={{ cx: -8, cy: -17, rx: 18, ry: 3.5, rot: -3, opacity: 0.55 }}
        />
        <path d="M-26 10 C -10 16, 12 16, 30 9 C 22 15, 6 17, -6 17 C -16 17, -22 14, -26 10 Z" fill={CREAM.base} opacity="0.9" />
        {/* Sele med guldkant och bjällror */}
        <path d="M-8 -22 C -12 -8, -12 4, -8 16" stroke={harness.shade} strokeWidth="6.5" fill="none" />
        <path d="M-8 -22 C -12 -8, -12 4, -8 16" stroke={harness.base} strokeWidth="5" fill="none" />
        <path d="M-6.5 -22 C -10.5 -8, -10.5 4, -6.5 16" stroke="#f7e2a6" strokeWidth="1" fill="none" />
        <Sphere cx={-9} cy={17} r={3.6} c={bell} />
        {name && <NameBand name={name} p={p} mirrored={mirrored} />}
        {/* Hals och bröstpäls */}
        <Shaded d="M20 -14 C 26 -28, 32 -40, 40 -46 L 56 -38 C 50 -30, 44 -16, 38 -2 Z" c={FUR} depth={5} />
        <Shaded d="M24 -8 q 5 -4 9 0 q 5 -2 7 3 q 4 4 1 8 q 1 5 -5 6 q -4 4 -8 0 q -5 -1 -5 -6 q -3 -5 1 -11 Z" c={CREAM} depth={3} />
        {/* Halsband med bjällror */}
        <path d="M24 -18 C 32 -10, 40 -12, 46 -24" stroke={harness.base} strokeWidth="4.5" fill="none" strokeLinecap="round" />
        {[29, 36, 43].map((bx, i) => (
          <Sphere key={i} cx={bx} cy={-13 - i * 2.4} r={2.6} c={bell} />
        ))}
        {/* Öra */}
        <Shaded d="M46 -58 C 36 -68, 29 -64, 31 -60 C 35 -55, 41 -55, 47 -55 Z" c={FUR} depth={2} />
        <path d="M43 -58 C 37 -63, 33 -61, 34 -59 C 36 -57, 40 -57, 43 -57 Z" fill="#f1b9a4" />
        {/* Horn med skugga och glans */}
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          <g stroke={ANTLER_SHADE} strokeWidth="4.2" transform="translate(1 1)">
            <path d="M48 -60 C 46 -72, 40 -80, 31 -85 M 43 -73 C 37 -73, 33 -76, 31 -80 M 45 -67 C 40 -66, 36 -64, 34 -61" />
            <path d="M55 -61 C 58 -74, 64 -82, 73 -85 M 62 -75 C 66 -78, 68 -82, 67 -87 M 59 -68 C 64 -68, 68 -66, 70 -63" />
          </g>
          <g stroke={ANTLER} strokeWidth="4.2">
            <path d="M48 -60 C 46 -72, 40 -80, 31 -85 M 43 -73 C 37 -73, 33 -76, 31 -80 M 45 -67 C 40 -66, 36 -64, 34 -61" />
            <path d="M55 -61 C 58 -74, 64 -82, 73 -85 M 62 -75 C 66 -78, 68 -82, 67 -87 M 59 -68 C 64 -68, 68 -66, 70 -63" />
          </g>
          <g stroke="#ffffff" strokeWidth="1.2" opacity="0.7">
            <path d="M46.5 -63 C 45 -72, 40 -79, 33 -83" />
            <path d="M54 -64 C 57 -73, 62 -80, 70 -83" />
          </g>
        </g>
        {/* Huvud */}
        <Shaded
          d="M40 -50 C 40 -60, 52 -64, 60 -60 C 66 -57, 72 -52, 76 -48 C 80 -44, 78 -38, 72 -38 C 64 -38, 58 -36, 52 -36 C 44 -36, 40 -42, 40 -50 Z"
          c={FUR}
          depth={5}
          highlight={{ cx: 50, cy: -58, rx: 6, ry: 2.4, rot: -12, opacity: 0.6 }}
        />
        <Shaded d="M62 -44 C 64 -50, 76 -50, 78 -44 C 79 -39, 74 -37, 68 -37 C 63 -37, 61 -40, 62 -44 Z" c={MUZZLE} depth={3} />
        <Eye x={55} y={-51} rx={3.6} ry={4.4} look={[0.45, -0.05]} />
        <path d="M50 -57 C 53 -59.5, 57 -59.5, 60 -57" stroke="#4a2616" strokeWidth="1.4" fill="none" strokeLinecap="round" />
        <Blush x={57} y={-43} r={3.2} color="#ff9a8a" opacity={0.5} />
        {lead ? (
          <>
            <circle cx={80} cy={-44} r={14} fill={p[400]} opacity={0.4} filter="url(#toon-softer)" className="hol-breathe" />
            <Sphere cx={80} cy={-44} r={5} c={{ base: p[500], shade: p[800], light: p[200], rim: p[600] }} />
          </>
        ) : (
          <Sphere cx={79} cy={-44} r={3.8} c={{ base: "#4a2a1e", shade: "#200e08", light: "#9a6a54" }} />
        )}
        <path d="M68 -38 C 71 -35.5, 74 -35.5, 76 -38" stroke="#5a2a1a" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        {/* Närmre benen */}
        <GallopLeg x={26} y={6} kind="front" dur={dur} begin={b} />
        <GallopLeg x={-28} y={6} kind="back" dur={dur} begin={b - dur * 0.5} />
      </g>
    </g>
  );
};

/**
 * Tomten i sin släde med Rehngruppens logga, dragen av fyra renar.
 * `santa` styr tomten: "sit" sitter och vinkar, "dance" står upp och
 * dansar, "show" sitter första varvet och växlar sedan mellan breakdance
 * och vanlig dans varannat varv (bytena styrs av scenes.css och sker när
 * släden är utanför bild).
 */
export const SleighTeam: React.FC<{ p: Palette; mirrored?: boolean; santa?: "sit" | "dance" | "show" }> = ({ p, mirrored, santa = "sit" }) => {
  const lacquer: ShadeColors = { base: p[600], shade: p[900], light: p[300], rim: p[700] };
  const sack: ShadeColors = { base: "#8a3a30", shade: "#4a1812", light: "#c26a58" };
  const gold = "#f6dfa6";
  const deer = [
    { x: 268, y: 48, phase: 0.1, name: "God jul" },
    { x: 362, y: 34, phase: 0.45, name: "Oscar" },
    { x: 456, y: 44, phase: 0.25, name: "Erik" },
    { x: 552, y: 28, phase: 0.6, lead: true, name: "Måns" },
  ];
  return (
    <svg viewBox="-60 -100 720 230">
      <defs>
        <linearGradient id="xm-dust" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="xm-runner" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff6dc" />
          <stop offset="100%" stopColor="#c9a24e" />
        </linearGradient>
      </defs>
      {/* Stjärnstoft bakom släden */}
      <path d="M-60 104 C -30 96, -10 108, 8 104" stroke="url(#xm-dust)" strokeWidth="3" strokeLinecap="round" fill="none" />
      {range(12).map(i => (
        <Sparkle key={i} x={-56 + i * 6} y={104 + Math.sin(i * 1.9) * 8} r={1.2 + (i / 12) * 2.4} className={`hol-star-group hol-star-group-${i % 3}`} />
      ))}
      {/* Tömmar */}
      <path d="M156 44 C 196 48, 232 54, 262 44 M 156 44 C 236 32, 312 30, 356 30 M 156 44 C 272 42, 400 42, 450 40 M 156 44 C 322 26, 480 22, 546 24" stroke="#5a2a1a" strokeWidth="1.4" fill="none" opacity="0.9" />
      {deer.map((d, i) => (
        <FlyingDeer key={i} x={d.x} y={d.y} p={p} lead={d.lead} phase={d.phase} s={1.22} name={d.name} mirrored={mirrored} />
      ))}
      {/* Medar i guld */}
      <path d="M-6 114 L 150 114 C 176 114, 188 98, 184 84 C 181 74, 168 76, 171 86" stroke="url(#xm-runner)" strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M-6 112 L 150 112" stroke="#ffffff" strokeWidth="1.2" opacity="0.7" />
      <path d="M-6 114 C -14 114, -18 106, -12 102" stroke="url(#xm-runner)" strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M34 104 V 114 M 80 104 V 114 M 126 104 V 114" stroke="#c9a24e" strokeWidth="3.4" />
      {/* Säcken med paket */}
      <Shaded d="M2 50 C -8 26, 6 0, 30 -4 C 52 -6, 66 12, 62 50 Z" c={sack} depth={8} highlight={{ cx: 20, cy: 10, rx: 6, ry: 3, rot: -30, opacity: 0.4 }} />
      <Shaded d="M10 -14 h 20 v 18 h -20 Z" c={{ base: p[200], shade: p[400], light: "#ffffff" }} depth={3} />
      <rect x="18" y="-14" width="4" height="18" fill={p[700]} />
      <Shaded d="M32 -22 h 18 v 24 h -18 Z" c={{ base: "#ffffff", shade: "#dcdde6", light: "#ffffff" }} depth={3} />
      <rect x="39" y="-22" width="4" height="24" fill={p[600]} />
      <path d="M41 -22 c -6 -8, -12 -4, -8 0 M 41 -22 c 6 -8, 12 -4, 8 0" stroke={p[600]} strokeWidth="2.4" fill="none" />
      <path d="M6 2 C 22 8, 44 8, 60 0" stroke={sack.shade} strokeWidth="3" fill="none" />
      {/* Tomten */}
      {santa === "show" ? (
        <>
          <g className="hol-santa-sit">
            <SleighSanta />
          </g>
          <g className="hol-santa-dance">
            <g className="hol-santa-break">
              <BreakdanceSanta />
            </g>
            <g className="hol-santa-groove">
              <DancingSanta />
            </g>
          </g>
        </>
      ) : santa === "dance" ? (
        <DancingSanta />
      ) : (
        <SleighSanta />
      )}
      {/* Släden */}
      <Shaded
        d="M4 104 C -2 78, 2 50, 20 42 C 30 38, 38 46, 36 58 L 38 68 L 126 68 C 142 68, 154 58, 160 44 C 162 38, 170 38, 172 46 C 176 72, 162 98, 134 106 C 122 110, 40 110, 26 110 C 14 110, 6 108, 4 104 Z"
        c={lacquer}
        depth={11}
        highlight={{ cx: 70, cy: 74, rx: 46, ry: 3.5, opacity: 0.6 }}
      />
      {/* Guldornament och panel med logga */}
      <path d="M16 62 C 12 76, 14 94, 26 102 L 116 102 C 140 102, 156 88, 162 66" stroke={gold} strokeWidth="2.2" fill="none" opacity="0.95" />
      {/* Medaljong med Rehngruppens symbol */}
      <circle cx="90" cy="86" r="15" fill={p[800]} opacity="0.6" />
      <circle cx="90" cy="86" r="15" fill="none" stroke={gold} strokeWidth="1.6" />
      <circle cx="90" cy="86" r="12.2" fill="none" stroke={gold} strokeWidth="0.6" opacity="0.6" />
      <svg x="79" y="75" width="22" height="22" viewBox={REHN_SYMBOL_VIEWBOX}>
        <path d={REHN_SYMBOL_PATH} fill={gold} />
      </svg>
      <path d="M44 86 C 56 80, 66 82, 72 86 M 108 86 C 114 82, 124 80, 136 86" stroke={gold} strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.9" />
      <path d="M22 54 c -4 -8, 4 -14, 9 -9 c 3 3, 0 7, -3 6" stroke={gold} strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M166 50 c 4 -6, -2 -12, -6 -8" stroke={gold} strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <Sphere cx={20} cy={42} r={4.5} c={{ base: gold, shade: "#b8892e", light: "#fff8dc" }} />
    </svg>
  );
};
